// src/components/DiagnosisReport.jsx

import React from 'react';
import '../styles/DiagnosisReport.css';

// ✨ 1. 新增一個精簡版的時間格式化函數
const formatLocalTimestamp = (utcString) => {
  if (!utcString) return 'N/A';
  try {
    const isoString = utcString.replace(' (UTC)', '').replace(' ', 'T') + 'Z';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return utcString;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const regionName = timeZone.split('/').pop().replace(/_/g, ' ');
    
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} (${regionName})`;
  } catch (e) {
    return utcString; // 出錯時返回原始字串
  }
};

// 輔助函數：根據錯誤類型返回對應的 CSS class
const getPhonemeClass = (phoneme) => {
  if (!phoneme) return '';
  if (phoneme.isMatch) return 'phoneme-correct';
  if (phoneme.user === '-') return 'phoneme-omission';
  if (phoneme.target === '-') return 'phoneme-insertion';
  return 'phoneme-substitution';
};

const getColumnWidths = (word) => {
  if (!word || !word.phonemes) return [];
  const widths = [];
  for (let i = 0; i < word.phonemes.length; i++) {
    const p = word.phonemes[i];
    // 確保即使音素為 null 或 undefined 也能處理
    const targetLen = p.target?.length || 0;
    const userLen = p.user?.length || 0;
    widths.push(Math.max(targetLen, userLen, 1)); // 最小寬度為 1
  }
  return widths;
};

const padPhoneme = (phonemeStr, columnWidth) => {
  const str = phonemeStr || ''; // 處理 null 或 undefined
  const padding = ' '.repeat(Math.max(0, columnWidth - str.length));
  return str + padding;
};

const DiagnosisReport = ({ result, nextAction }) => {
  if (!result || !result.summary || !result.words) {
    return (
      <div className="report-card-v2">
        <p className="error-text">Analysis data is incomplete or invalid.</p>
      </div>
    );
  }

  const { summary, words, sentence, analysisTimestampUTC } = result;
  const incorrectWords = summary.totalWords - summary.correctWords;

  return (
    <div className="report-card-v2">
      <div className="report-header">
        <hr className="divider main" />
        <span className="header-text">Pronunciation Analysis</span>
        <hr className="divider main" />
      </div>

      {/* --- 音素對比 --- */}
      <div className="comparison-section-v2">
        <div className="analysis-line">
          <span className="line-label">Sentence:</span>
          <span className="line-content">{sentence}</span>
        </div>
        <div className="analysis-line">
          <span className="line-label">Target:</span>
          <div className="line-content phoneme-wrapper">
            {words.map((word, wordIdx) => {
              // ✨ 預先計算該單詞的列寬
              const columnWidths = getColumnWidths(word);
              return (
                <span key={`target-word-${wordIdx}`} className="word-block">
                  [ {word.phonemes?.map((p, pIdx) => (
                    // ✨ 使用 padPhoneme 進行填充
                    <span key={`t-p-${pIdx}`} className="phoneme">{padPhoneme(p.target, columnWidths[pIdx])}</span>
                  )).reduce((prev, curr) => [prev, ' ', curr])} ]
                </span>
              );
            })}
          </div>
        </div>
        <div className="analysis-line">
          <span className="line-label">User:</span>
          <div className="line-content phoneme-wrapper">
            {words.map((word, wordIdx) => {
              // ✨ 預先計算該單詞的列寬
              const columnWidths = getColumnWidths(word);
              return (
                <span key={`user-word-${wordIdx}`} className="word-block">
                  [ {word.phonemes?.map((p, pIdx) => (
                    // ✨ 使用 padPhoneme 進行填充
                    <span key={`u-p-${pIdx}`} className={`phoneme ${getPhonemeClass(p)}`}>{padPhoneme(p.user, columnWidths[pIdx])}</span>
                  )).reduce((prev, curr) => [prev, ' ', curr])} ]
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* --- 摘要資訊 --- */}
      <div className="report-header">
        <hr className="divider sub" />
        <span className="header-text">[ Summary ]</span>
        <hr className="divider sub" />
      </div>
      <div className="summary-details">
        <div>- Overall Score:      {summary.overallScore.toFixed(1)}%</div>
        <div>- Total Words:        {summary.totalWords}</div>
        <div>- Correct Words:      {summary.correctWords}</div>
        <div>- Incorrect Words:    {incorrectWords}</div>
        <div>- Phoneme Error Rate: {summary.phonemeErrorRate.toFixed(2)}% ({summary.total_errors} errors in {summary.total_target_phonemes} phonemes)</div>
        {/* ✨ 2. 【【【唯一修改點】】】✨ */}
        <div>- Analysis Timestamp: {formatLocalTimestamp(analysisTimestampUTC)}</div>
      </div>
      
      {nextAction && (
        <div className="report-action-footer">
          {nextAction}
        </div>
      )}
    </div>
  );
};

export default DiagnosisReport;
